import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Alert,
    Divider,
    TextField,
    InputAdornment,
} from "@mui/material";
import {
    RequestQuote as QuoteIcon,
    Search as SearchIcon,
    CheckCircle as CompleteIcon,
    ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import QuoteService from "../services/Quote.service";

function QuoteCard({ quote, onClick }) {
    return (
        <Card variant="outlined" sx={{ borderColor: "primary.main" }}>
            <CardActionArea onClick={onClick}>
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                <Typography
                                    variant="subtitle2"
                                    fontWeight={700}
                                    sx={{ fontFamily: "Montserrat, sans-serif" }}
                                >
                                    {quote.number}
                                </Typography>
                                <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
                                <Typography variant="subtitle2" noWrap>
                                    {quote.name}
                                </Typography>
                            </Box>
                            {quote.customers_detail?.length > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                    {quote.customers_detail.map((c) => c.name).join(", ")}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                            {quote.is_complete && (
                                <Chip
                                    icon={<CompleteIcon />}
                                    label="Won"
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                />
                            )}
                            {quote.is_archived && !quote.is_complete && (
                                <Chip label="Archived" size="small" variant="outlined" />
                            )}
                            <ChevronRightIcon fontSize="small" color="action" />
                        </Box>
                    </Box>
                </CardContent>
            </CardActionArea>
        </Card>
    );
}

function SubcontractorQuotes() {
    const navigate = useNavigate();
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");

    useEffect(() => {
        QuoteService.list({ page_size: 200 })
            .then((res) => setQuotes(res.data?.results || res.data || []))
            .catch(() => setError("Failed to load quotes."))
            .finally(() => setLoading(false));
    }, []);

    const filtered = quotes.filter((q) => {
        const term = search.toLowerCase();
        return (
            !term ||
            q.name?.toLowerCase().includes(term) ||
            q.number?.toLowerCase().includes(term) ||
            q.customers_detail?.some((c) => c.name?.toLowerCase().includes(term))
        );
    });

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "Montserrat, sans-serif" }}>
                    My Quotes
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Quotes linked to your company
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {!loading && quotes.length > 1 && (
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search quotes…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
            )}

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
                    <CircularProgress />
                </Box>
            ) : filtered.length === 0 ? (
                <Box sx={{ textAlign: "center", mt: 8, color: "text.secondary" }}>
                    <QuoteIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                    <Typography variant="body2">
                        {quotes.length === 0
                            ? "No quotes have been linked to your company yet."
                            : "No quotes match your search."}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {filtered.map((q) => (
                        <QuoteCard key={q.id} quote={q} onClick={() => navigate(`/subcontractor/quotes/${q.id}`)} />
                    ))}
                </Box>
            )}
        </Box>
    );
}

export default SubcontractorQuotes;
